export const VALIDATION_RULES = {
    firstName: {
        required: true,
        maxLength: 30
    },

    lastName: {
        required: true,
        maxLength: 30
    },

    address1: {
        required: true,
        minLength: 2,
        maxLength: 30
    },

    address2: {
        required: true,
        maxLength: 10,
        pattern: /^[0-9]+$/
    },

    c_address3: {
        // No required - this field is optional
        maxLength: 20
    },

    city: {
        required: true,
        minLength: 2,
        maxLength: 50
    },

    companyName: {
        // No required - this field is optional
        maxLength: 30
    }
}

/**
 * Postal code validation rules by country
 */
const POSTAL_CODE_RULES = {
    AT: {
        // Austria: Max 4 digits, e.g., 4535
        maxLength: 4,
        pattern: /^[0-9]{4}$/
    },
    NL: {
        // Netherlands: 4 digits + 2 letters, e.g., 4818BJ or 4818 BJ
        maxLength: 7,
        pattern: /^[0-9]{4}\s?[A-Z]{2}$/i
    },
    DE: {
        // Germany: Max 5 digits, e.g., 45354
        maxLength: 5,
        pattern: /^[0-9]{1,5}$/
    },
    BE: {
        // Belgium: Max 4 digits, e.g., 4535
        maxLength: 4,
        pattern: /^[0-9]{4}$/
    },
    INT: {
        // International: Max 10 characters, e.g., 4534 HH or AK 23
        maxLength: 10,
        pattern: /^.{1,10}$/
    }
}

/**
 * Get validation rules for a specific field
 * @param {string} fieldName - The field name to get rules for
 * @returns {object} Validation rules object
 */
export const getValidationRules = (fieldName) => {
    return VALIDATION_RULES[fieldName] || {}
}

/**
 * Create formatted validation rules with specific error messages for character limits
 * @param {string} fieldName - The field name
 * @param {function} formatMessage - The intl formatMessage function
 * @returns {object} Formatted validation rules
 */
export const createFormattedValidationRules = (fieldName, messages) => {
    const rules = VALIDATION_RULES[fieldName]
    if (!rules) return {}

    const {required, invalid, minLength, maxLength} = messages
    const formattedRules = {}

    if (rules.required) {
        formattedRules.required = required
    }

    if (rules.minLength) {
        formattedRules.minLength = {
            value: rules.minLength,
            message: minLength?.replace('{0}', rules.minLength)
        }
    }

    if (rules.maxLength) {
        formattedRules.maxLength = {
            value: rules.maxLength,
            message: maxLength?.replace('{0}', rules.maxLength)
        }
    }

    if (rules.pattern) {
        formattedRules.pattern = {
            value: rules.pattern,
            message: invalid
        }
    }

    return formattedRules
}

/**
 * Get postal code validation rules based on country
 * @param {string} countryCode - The country code (e.g., 'AT', 'NL', 'DE', 'BE')
 * @param {function} formatMessage - The intl formatMessage function
 * @returns {object} Formatted validation rules for postal code
 */
export const getPostalCodeValidationRules = (countryCode, messages) => {
    // Determine country group
    const specificCountries = ['AT', 'NL', 'DE', 'BE']
    const country = specificCountries.includes(countryCode) ? countryCode : 'INT'
    const {required, invalid} = messages

    const rules = POSTAL_CODE_RULES[country]
    const formattedRules = {}

    // Required field message
    const requiredMessage = required

    // Invalid format message
    const invalidFormatMessage = invalid

    // Required field
    formattedRules.required = requiredMessage

    // Apply validation rules based on country
    if (rules.maxLength) {
        formattedRules.maxLength = {
            value: rules.maxLength,
            message: invalidFormatMessage
        }
    }

    if (rules.pattern) {
        formattedRules.pattern = {
            value: rules.pattern,
            message: invalidFormatMessage
        }
    }

    return formattedRules
}

/**
 * Get phone field configuration based on country (rules + label info)
 * @param {string} countryCode - The country code (e.g., 'AT', 'NL', 'DE', 'BE')
 * @param {function} formatMessage - The intl formatMessage function
 * @returns {object} Phone field configuration with rules and label info
 */
export const getPhoneFieldConfig = (countryCode, messages) => {
    // Determine if country requires mandatory phone
    const optionalPhoneCountries = ['AT', 'NL', 'DE', 'BE']
    const isOptional = optionalPhoneCountries.includes(countryCode)
    const {required, invalid} = messages

    // Required field message
    const requiredMessage = required

    // Invalid format message
    const invalidFormatMessage = invalid

    // Build validation rules
    const rules = {}

    // Required field (only for INT countries)
    rules.required = !isOptional ? requiredMessage : false

    // Numbers only pattern for all countries
    rules.pattern = {
        value: /^[0-9\s\+\-\(\)]*$/,
        message: invalidFormatMessage
    }

    return {
        rules,
        isOptional
    }
}

/**
 * Get phone validation rules based on country (backward compatibility)
 * @param {string} countryCode - The country code (e.g., 'AT', 'NL', 'DE', 'BE')
 * @param {function} formatMessage - The intl formatMessage function
 * @returns {object} Formatted validation rules for phone
 */
export const getPhoneValidationRules = (countryCode, formatMessage) => {
    return getPhoneFieldConfig(countryCode, formatMessage).rules
}
